import { useLocalStorage } from '@vueuse/core'

import type { ListsStore, EnsureOptions } from './interfaces'
import type { Lists } from '@brighterly/lib-core-types/lists/interfaces'
import type { Offer } from '@brighterly/lib-core-types/plans/interfaces'

import { useListsApi } from '@api/configurations/lists.hook'
import { useTimeSlotsApi } from '@api/configurations/time-slots.hook'

const STORAGE_KEY = 'lists'

const persist = (state: ListsStore): void => {
  const ls = useLocalStorage<Lists | null>(STORAGE_KEY, null, {
    serializer: {
      read: (v) => (v ? JSON.parse(v) : null),
      write: (v) => (v ? JSON.stringify(v) : ''),
    },
  })
  const discipline = toRaw(state.lists) as Lists | null

  ls.value = discipline ? { ...discipline } : null
}

const setLists = (state: ListsStore, lists: ListsStore['lists']) => {
  state.lists = toRaw(lists)
  persist(state)
}

const clear = (state: ListsStore): void => {
  state.lists = null
  persist(state)
}

export const actions = {
  hydrateFromStorage (this: ListsStore): void {
    if (this._hydrated) return

    const ls = useLocalStorage<Lists | null>(STORAGE_KEY, null, {
      serializer: {
        read: (v) => (v ? JSON.parse(v) : null),
        write: (v) => (v ? JSON.stringify(v) : ''),
      },
    })

    this.lists = ls.value ? { ...ls.value } : null
    this._hydrated = true
  },

  setLists (this: ListsStore, lists: ListsStore['lists']): void {
    return setLists(this, lists)
  },

  async ensure (this: ListsStore, options: EnsureOptions = {}): Promise<ListsStore['lists']> {
    if (!options.force && this.lists) {
      return this.lists
    }

    if (this.lists && options.background) {
      void useListsApi()

      return this.lists
    }

    return await useListsApi()
  },

  async ensureTimeSlots (this: ListsStore, options: EnsureOptions = {}): Promise<ListsStore['timeSlots']> {
    if (!options.force && this.timeSlots) {
      return this.timeSlots
    }

    if (this.timeSlots && options.background) {
      void useTimeSlotsApi()

      return this.timeSlots
    }

    return await useTimeSlotsApi()
  },

  persist (this: ListsStore): void {
    return persist(this)
  },

  async refreshNow (this: ListsStore): Promise<ListsStore['lists']> {
    return await useListsApi()
  },

  clear (this: ListsStore): void {
    return clear(this)
  },

  setTimeSlots (this: ListsStore, timeSlots: ListsStore['timeSlots']): void {
    this.timeSlots = timeSlots
  },

  setOffer (this: ListsStore, offer: Offer): void {
    this.offer = offer
  },
  setTimeSlotsLoading (this: ListsStore, payload: ListsStore['timeSlotsLoading']): void {
    this.timeSlotsLoading = payload
  },
}
